/**
 * `ReadinessHeader` — the page's verdict headline + rescan controls.
 *
 * 2026-10-02 rewrite:
 *   - Removed the kicker line with the domain + platform-kind badge.
 *   - H1 is the VERDICT ("Ready for AI agents.", "Almost ready for AI
 *     agents.", "Not ready for AI agents yet.", or while loading
 *     "Checking how ready you are for AI agents.").
 *   - Sub-line underneath: "<N> things to fix · <V> AI agent visits
 *     this week", where the visits segment links to the Agents view
 *     filtered to AI; omitted when visits are unknown; "Nothing to
 *     fix" variant when 0.
 *   - Meta line shows "Updating…" while the slow sources refresh
 *     after a cache paint; the "Checked <time>" still reflects the
 *     time of the data currently shown.
 *
 * The visit-count headline + top-agent subline moved to the Agents view.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, RefreshCw } from 'lucide-react';

import type { AgentOverviewResponse, SiteRescanCadence, SiteRescanSchedule } from '@sill/types/api';
import type { ReadinessVerdict } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { putSiteRescanSchedule } from '../../api/site-rescan-schedule.js';
import { useC } from '../../lib/theme.js';
import { formatRelativeTime } from '../../lib/format.js';
import { Dash, MonoLink } from './MonoLink.js';
import { ReadinessRing } from './ReadinessRing.js';
import { monoFont, sansFont, serifFont } from './fonts.js';

export interface ReadinessHeaderProps {
  readonly overview: AgentOverviewResponse | null;
  readonly install_null: boolean;
  readonly checked_at: string | null;
  readonly rescan: SiteRescanSchedule | null;
  readonly onRescanChange: (next: SiteRescanSchedule) => void;
  readonly onRefresh: () => void;
  readonly refreshing: boolean;
  readonly site_id: string;
  readonly verdict: ReadinessVerdict;
  readonly verdict_loading: boolean;
  /** Shown as a quiet "Updating…" chip in the meta line while the
   *  background refresh is still in flight (after a cache paint, or
   *  during an in-flight user-driven re-check). */
  readonly updating: boolean;
  readonly background_refreshing: boolean;
  /** Open failing-step count. `null` while any primary source is
   *  still loading so the sub-line stays honest. 0 renders the
   *  "Nothing to fix" variant. */
  readonly fix_count: number | null;
  /** AI agent visits in the last 7-day window — rendered as a link
   *  that opens the Agents view filtered to AI. `null` omits the
   *  visits segment entirely. */
  readonly ai_visits: number | null;
  readonly onViewAgentVisits: () => void;
  readonly signals_refresh_failed: boolean;
  readonly signals_loaded_at: string | null;
  /** Opens the "What's left" drawer with the colour explainer and the
   *  grouped failing checks. Click target on the ring and the
   *  "N things to fix" sub-line both call this. */
  readonly onExplainWhatsLeft?: () => void;
  /** False when the site has NO completed readiness snapshot. The
   *  header must then show the "Not checked yet" (or "Checking…")
   *  state instead of a verdict — no fraction, no "Checked X ago",
   *  no sub-line "Nothing to fix". */
  readonly has_snapshot?: boolean;
  /** True when a readiness run is in flight right now. Changes the
   *  no-snapshot headline to "Checking your site…". */
  readonly run_in_progress?: boolean;
}

const CADENCES: ReadonlyArray<{ value: SiteRescanCadence; label: string }> = [
  { value: 'manual', label: 'manually' },
  { value: 'daily', label: 'daily' },
  { value: 'weekly', label: 'weekly' },
  { value: 'monthly', label: 'monthly' },
];

function cadenceWord(c: SiteRescanCadence | undefined): string {
  const m = CADENCES.find((x) => x.value === c);
  return m?.label ?? 'manually';
}

/** Exported for unit testing — the pure verdict → headline mapping
 *  with the never-checked / in-progress overrides. */
export function verdictHeadlineForTests(
  verdict: ReadinessVerdict,
  loading: boolean,
  has_snapshot: boolean,
  run_in_progress: boolean,
): string {
  return verdictHeadline(verdict, loading, has_snapshot, run_in_progress);
}

function verdictHeadline(
  verdict: ReadinessVerdict,
  loading: boolean,
  has_snapshot: boolean,
  run_in_progress: boolean,
): string {
  if (!has_snapshot) {
    if (run_in_progress || loading) return 'Checking your site…';
    return 'Not checked yet.';
  }
  if (loading) return 'Checking how ready you are for AI agents.';
  if (verdict.kind === 'agent_ready') return 'Ready for AI agents.';
  if (verdict.kind === 'almost_ready') return 'Almost ready for AI agents.';
  return 'Not ready for AI agents yet.';
}

export function ReadinessHeader({
  overview, install_null, checked_at,
  rescan, onRescanChange, onRefresh, refreshing, site_id,
  verdict, verdict_loading, updating, background_refreshing,
  fix_count, ai_visits, onViewAgentVisits,
  signals_refresh_failed, signals_loaded_at,
  onExplainWhatsLeft,
  has_snapshot = true,
  run_in_progress = false,
}: ReadinessHeaderProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (popRef.current === null) return;
      if (!popRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function pickCadence(c: SiteRescanCadence): Promise<void> {
    if (http === null) return;
    setSaving(true);
    const r = await putSiteRescanSchedule(http, site_id, c);
    setSaving(false);
    if (r.kind === 'ok') onRescanChange(r.schedule);
    setOpen(false);
  }

  const headline = verdictHeadline(verdict, verdict_loading, has_snapshot, run_in_progress);

  // Sub-line parts. When the site has no completed snapshot the entire
  // sub-line is suppressed — the merchant must click "Check again" to
  // get a verdict + counts.
  const fixPart = !has_snapshot
    ? null
    : fix_count === null
      ? null
      : fix_count === 0
        ? 'Nothing to fix'
        : fix_count === 1
          ? '1 thing to fix'
          : `${fix_count.toLocaleString('en-US')} things to fix`;
  const visitsPart = !has_snapshot
    ? null
    : ai_visits === null
      ? null
      : ai_visits === 1
        ? '1 AI agent visit this week'
        : `${ai_visits.toLocaleString('en-US')} AI agent visits this week`;

  // Mono "Updating…" chip in the meta line. Lives alongside "Checked"
  // + "Rescans"; keeps the mono rhythm. The quiet style keeps lime
  // reserved for the primary button on the page.
  const showUpdating = updating || background_refreshing;

  // When install coverage comes back null AND we have no overview to
  // play from, the full verdict copy still applies (the page's job is
  // to answer "are you agent-ready?"), but we add a short install-null
  // kicker underneath the sub-line to point the merchant to Install.
  void install_null; // deliberately not surfaced here — the "N things to fix" sub-line covers it.

  return (
    <header
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
      style={{ fontFamily: sansFont }}
    >
      <div className="flex flex-col gap-3 min-w-0">
        <h1 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(32px, 5vw, 46px)', color: C.text, lineHeight: 1.08 }}>
          {headline}
        </h1>
        {(fixPart !== null || visitsPart !== null) ? (
          <div style={{ fontSize: 15, color: C.body }}>
            {fixPart !== null && typeof onExplainWhatsLeft === 'function' && (fix_count ?? 0) > 0 ? (
              <button
                type="button"
                onClick={onExplainWhatsLeft}
                aria-label={`See what's left (${fixPart.toLowerCase()})`}
                data-testid="readiness-fix-count-explain"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: C.text,
                  textDecoration: 'underline',
                  textDecorationColor: C.dim,
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                  fontFamily: sansFont,
                  fontSize: 15,
                }}
              >
                {fixPart}
              </button>
            ) : fixPart !== null ? fixPart : ''}
            {fixPart !== null && visitsPart !== null ? ' · ' : ''}
            {visitsPart !== null ? (
              <button
                type="button"
                onClick={onViewAgentVisits}
                aria-label={`See AI agent visits (${visitsPart.toLowerCase()})`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: C.text,
                  textDecoration: 'underline',
                  textDecorationColor: C.dim,
                  textUnderlineOffset: '3px',
                  cursor: 'pointer',
                  fontFamily: sansFont,
                  fontSize: 15,
                }}
              >
                {visitsPart}
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-col gap-2" style={{ marginTop: 6 }}>
          <ReadinessRing
            verdict={verdict}
            loading={verdict_loading || (!has_snapshot && run_in_progress)}
            no_verdict={!has_snapshot && !run_in_progress}
            {...(typeof onExplainWhatsLeft === 'function' ? { onExplain: onExplainWhatsLeft } : {})}
          />
        </div>
      </div>
      <div className="flex flex-col items-start md:items-end gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2"
          aria-label="Check again"
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            padding: '10px 18px',
            borderRadius: 3,
            border: `1px solid ${C.border}`,
            background: 'transparent',
            color: C.text,
            cursor: refreshing ? 'default' : 'pointer',
            opacity: refreshing ? 0.6 : 1,
            minHeight: 44,
          }}
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : undefined} aria-hidden="true" /> Check again
        </button>
        <div
          data-testid="readiness-meta"
          style={{ fontFamily: monoFont, fontSize: 12, color: C.muted, position: 'relative' }}
          ref={popRef}
        >
          {!has_snapshot ? (
            <>Rescans <MonoLink onClick={() => setOpen((v) => !v)} aria-label="Change rescan cadence">{cadenceWord(rescan?.rescan_cadence)}</MonoLink></>
          ) : checked_at === null ? (
            <>Checked <Dash /> · Rescans <MonoLink onClick={() => setOpen((v) => !v)}>{cadenceWord(rescan?.rescan_cadence)}</MonoLink></>
          ) : (
            <>Checked {formatRelativeTime(checked_at)} · Rescans <MonoLink onClick={() => setOpen((v) => !v)} aria-label="Change rescan cadence">{cadenceWord(rescan?.rescan_cadence)}</MonoLink></>
          )}
          {showUpdating ? (
            <span
              aria-live="polite"
              data-testid="readiness-updating"
              style={{ marginLeft: 10, color: C.dim }}
            >
              · Updating…
            </span>
          ) : null}
          {signals_refresh_failed ? (
            <div style={{ marginTop: 4, color: C.body }} aria-live="polite">
              Couldn&apos;t refresh just now.
              {signals_loaded_at !== null
                ? ` Showing results from ${formatRelativeTime(signals_loaded_at)}.`
                : ''}
            </div>
          ) : null}
          {open ? (
            <div
              role="dialog"
              aria-label="Rescan cadence"
              style={{
                position: 'absolute',
                right: 0,
                top: 22,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                padding: 6,
                minWidth: 160,
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {CADENCES.map((c) => {
                const active = rescan?.rescan_cadence === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => void pickCadence(c.value)}
                    disabled={saving}
                    className="inline-flex items-center justify-between"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      padding: '8px 10px',
                      borderRadius: 2,
                      border: 'none',
                      background: active ? C.surface2 : 'transparent',
                      color: active ? C.text : C.body,
                      cursor: saving ? 'default' : 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      minHeight: 36,
                    }}
                  >
                    <span>{c.label}</span>
                    {active ? <Check size={12} color={C.text} aria-hidden="true" /> : null}
                  </button>
                );
              })}
              <div style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, padding: '4px 10px' }}>
                <ChevronDown size={10} aria-hidden="true" /> change anytime
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
